import { DocBreadcrumbs } from '../components/DocBreadcrumbs';
import { DocPagination } from '../components/DocPagination';
import { PagePurpose } from '../components/PagePurpose';
import { StatusBadge } from '../components/StatusBadge';
import { MAPHUB_URL } from '../config';

const palette = {
  accent: '#0F6B7C',
  border: '#D4E2F4',
  tint: '#F4F7FC',
  heroDark: '#4A97AC',
  heroLight: '#D4E2F4',
};

const accessPaths = [
  {
    title: 'Browser',
    who: 'All team members',
    description:
      'Open maphub.studiobereikbaar.nl, log in with your SB Microsoft account, browse maps, toggle layers, view attributes, and compare datasets. No software install needed.',
    badge: 'Laagdrempelig',
  },
  {
    title: 'QGIS Plugin',
    who: 'Power users (GIS analysts)',
    description:
      'Install the MapHub plugin in QGIS, connect to our self-hosted server with your API key, and sync maps directly between your desktop and the platform. Upload, download, and version your work.',
    badge: 'Advanced',
  },
];

const workspaceFolders = [
  { name: 'SB Projects', detail: 'Project folders — client deliverables and active analyses' },
  { name: 'Gio Labs', detail: 'Experimental analyses and development work' },
  { name: 'BRT Achtergrond', detail: 'BRT topographic base layers for map styling' },
];

export function MapHubPage() {
  return (
    <div className="space-y-8">
      <DocBreadcrumbs currentTitle="MapHub" />

      <PagePurpose
        title="MapHub — a door to your maps, not a second store"
        audience="All team members using maps in their work"
        description="MapHub is our self-hosted way to browse and sync geospatial data. Think of it as a door onto the maps we already produce — open them in the browser or connect via QGIS."
        badge="Self-hosted"
      />

      <section
        className="rounded-3xl text-white shadow-xl"
        style={{
          background: `linear-gradient(135deg, ${palette.heroDark} 0%, ${palette.heroLight} 100%)`,
        }}
      >
        <div className="p-8">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-white/80">
            Self-hosted platform
          </p>
          <h1 className="mt-4 text-4xl font-bold leading-tight">MapHub</h1>
          <p className="mt-4 max-w-3xl text-lg leading-relaxed text-white/90">
            Geospatial collaboration at maphub.studiobereikbaar.nl. Browse maps in the browser,
            sync through QGIS, and share project data with the team — all on our own infrastructure.
          </p>
          <div className="mt-4 flex flex-wrap gap-3 text-sm font-semibold">
            <span className="rounded-full bg-white/20 px-4 py-2">Self-hosted since Feb 2026</span>
            <span className="rounded-full bg-white/20 px-4 py-2">SB Microsoft SSO</span>
            <span className="rounded-full bg-white/20 px-4 py-2">Browser + QGIS plugin</span>
          </div>
        </div>
      </section>

      {/* A door, not a store */}
      <section className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.border }}>
        <h2 className="text-2xl font-semibold text-sb-black">A door, not a store</h2>
        <p className="mt-3 max-w-3xl text-base text-sb-dark-grey">
          MapHub is a way in to maps — a door you open in the browser or through the QGIS plugin. It
          isn't meant to become a second copy of our data. The registry stays the single source of
          truth; MapHub is how people reach and share the maps built from it.
        </p>
        <div
          className="mt-5 rounded-2xl border p-5"
          style={{ borderColor: palette.border, backgroundColor: palette.tint }}
        >
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-semibold text-sb-black">Zero-copy publication</h3>
            <StatusBadge status="building" />
          </div>
          <p className="mt-2 text-sm text-sb-dark-grey">
            The goal: publish OGC process results to MapHub without duplicating the bytes — a tile URL
            that points back at the registry rather than a new stored copy. This is being built; it's
            not done yet.
          </p>
        </div>
      </section>

      <section className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.border }}>
        <h2 className="text-2xl font-semibold text-sb-black">Why self-hosted?</h2>
        <p className="mt-3 text-base text-sb-dark-grey">
          The MapHub cloud service (app.maphub.co) is being discontinued. In February 2026 we migrated
          our map library to our own server at maphub.studiobereikbaar.nl, running on Hetzner
          infrastructure. That means full control over our data, no dependency on an external service,
          and the option to offer MapHub hosting to other companies as a service.
        </p>
      </section>

      <section className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.border }}>
        <h2 className="text-2xl font-semibold text-sb-black">Access</h2>
        <p className="mt-3 text-sm text-sb-dark-grey">
          Log in at{' '}
          <a
            href={MAPHUB_URL}
            className="font-semibold hover:underline"
            style={{ color: palette.accent }}
            target="_blank"
            rel="noopener noreferrer"
          >
            maphub.studiobereikbaar.nl
          </a>{' '}
          with your Studio Bereikbaar Microsoft account (SSO). No separate credentials needed.
        </p>
      </section>

      <section className="rounded-3xl border bg-white p-6 shadow-sm" style={{ borderColor: palette.border }}>
        <h2 className="text-2xl font-semibold text-sb-black">Two paths to your data</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {accessPaths.map((path) => (
            <div
              key={path.title}
              className="rounded-2xl border p-5"
              style={{ borderColor: palette.border, backgroundColor: palette.tint }}
            >
              <div className="flex items-center gap-3">
                <span
                  className="rounded-full border px-3 py-1 text-xs font-semibold"
                  style={{ borderColor: palette.border, color: palette.accent }}
                >
                  {path.badge}
                </span>
                <p className="text-xs text-gray-500">{path.who}</p>
              </div>
              <h3 className="mt-2 text-lg font-semibold text-sb-black">{path.title}</h3>
              <p className="mt-2 text-sm text-sb-dark-grey">{path.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border bg-white p-6 shadow-sm" style={{ borderColor: palette.border }}>
        <h2 className="text-2xl font-semibold text-sb-black">Workspace overview</h2>
        <p className="mt-2 text-sm text-sb-dark-grey">
          Our self-hosted instance organizes maps into project folders. Each folder can contain
          multiple maps with versioned layers.
        </p>
        <div className="mt-4 space-y-3">
          {workspaceFolders.map((folder) => (
            <div
              key={folder.name}
              className="flex items-center justify-between rounded-2xl border px-4 py-3"
              style={{ borderColor: palette.border }}
            >
              <div>
                <p className="text-sm font-semibold text-sb-black">{folder.name}</p>
                <p className="text-xs text-sb-dark-grey">{folder.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border bg-white p-6 shadow-sm" style={{ borderColor: palette.border }}>
        <h2 className="text-2xl font-semibold text-sb-black">Getting started — QGIS Plugin</h2>
        <p className="mt-2 text-sm text-sb-dark-grey">
          Short intro to the MapHub QGIS Plugin workflow. For detailed step-by-step instructions,
          see the Dutch setup guide (PDF) distributed to the team.
        </p>
        <div className="mt-4 aspect-video w-full overflow-hidden rounded-2xl border" style={{ borderColor: palette.border }}>
          <iframe
            src="https://www.youtube.com/embed/Spd29WJLGGM"
            title="MapHub QGIS Plugin Introduction"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="h-full w-full"
          />
        </div>
        <p className="mt-3 text-xs text-sb-dark-grey">
          Setup guide (Dutch, PDF): available from Giovanni — covers login, plugin install,
          server connection with API key, Browse/Sync workflow, and troubleshooting.
        </p>
      </section>

      <DocPagination currentPageId="maphub" />
    </div>
  );
}
